// src/pages/Notifications.tsx
import React, { useEffect, useState } from "react";
import {
    Box, Typography, Paper, Tabs, Tab, TextField, Button, Grid,
    FormControl, InputLabel, Select, MenuItem, Stack, CircularProgress,
    Snackbar, Alert, Chip, Autocomplete, Divider,
} from "@mui/material";
import {
    Send as SendIcon, Campaign as BroadcastIcon, Person as PersonIcon,
    Group as GroupIcon, Image as ImageIcon, FilterAlt as FilterIcon,
    NotificationsActive as NotifIcon, Clear as ClearIcon,
    Smartphone as PhoneIcon,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";

import { getClasses, getMediums, getSubjects } from "../services/subjectService";
import { getUsers, User } from "../services/userService";
import {
    sendSingleNotification, sendBulkNotification,
    sendUserListNotification, BulkNotificationData,
} from "../services/notificationService";

const PRIMARY = "#174143";
const ACCENT = "#3d9ea4";

const fadeUp = keyframes`
  from { opacity:0; transform:translateY(12px); }
  to   { opacity:1; transform:translateY(0); }
`;

// ── Reusable phone preview component ────────────────────
function NotifPreview({ title, body, imageUrl }: { title: string; body: string; imageUrl?: string }) {
    return (
        <Box sx={{ animation: `${fadeUp} 0.3s ease-out` }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
                sx={{ textTransform: "uppercase", letterSpacing: 0.5, mb: 1.5, display: "block" }}>
                Live Preview
            </Typography>
            {/* Phone frame */}
            <Box sx={{
                position: "relative", maxWidth: 280, mx: "auto",
                background: "#1a1a2e", borderRadius: 5, p: 2.5, pb: 4,
                boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
            }}>
                {/* Notch */}
                <Box sx={{ width: 60, height: 6, background: "#333", borderRadius: 3, mx: "auto", mb: 2 }} />
                {/* Status bar */}
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, px: 0.5 }}>
                    <Typography sx={{ fontSize: "0.65rem", color: "#aaa" }}>9:41 AM</Typography>
                    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                        {[3, 5, 7].map(h => <Box key={h} sx={{ width: 3, height: h, background: "#aaa", borderRadius: 1 }} />)}
                    </Box>
                </Box>
                {/* Notification card */}
                <Box sx={{
                    background: "rgba(255,255,255,0.12)", borderRadius: 2.5, p: 1.5,
                    backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.08)",
                }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Box sx={{
                            width: 32, height: 32, borderRadius: 1.5, flexShrink: 0,
                            background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                            <NotifIcon sx={{ fontSize: 16, color: "#fff" }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
                                <Typography sx={{ fontSize: "0.67rem", color: "#ddd", fontWeight: 700 }} noWrap>
                                    Vidya Samruddhi
                                </Typography>
                                <Typography sx={{ fontSize: "0.6rem", color: "#aaa" }}>now</Typography>
                            </Box>
                            <Typography sx={{ fontSize: "0.75rem", color: "#fff", fontWeight: 700, lineHeight: 1.3 }} noWrap>
                                {title || "Notification Title"}
                            </Typography>
                            <Typography sx={{ fontSize: "0.68rem", color: "#ccc", lineHeight: 1.4, mt: 0.3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                {body || "Your message will appear here…"}
                            </Typography>
                        </Box>
                    </Stack>
                    {imageUrl && (
                        <Box component="img" src={imageUrl}
                            sx={{ width: "100%", height: 100, objectFit: "cover", borderRadius: 1.5, mt: 1 }} />
                    )}
                </Box>
                <Box sx={{ width: 40, height: 4, background: "#555", borderRadius: 2, mx: "auto", mt: 2 }} />
            </Box>
        </Box>
    );
}

// ── Section label ────────────────────────────────────────
function SectionLabel({ icon, children }: { icon: React.ReactNode; children: string }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
            <Box sx={{ color: ACCENT }}>{icon}</Box>
            <Typography variant="caption" fontWeight={800} color="text.secondary"
                sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                {children}
            </Typography>
        </Box>
    );
}

export default function NotificationsPage(): React.ReactElement {
    const [tabIndex, setTabIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState<{ type: "success" | "error"; msg: string } | null>(null);

    // Meta
    const [classes, setClasses] = useState<any[]>([]);
    const [mediums, setMediums] = useState<any[]>([]);
    const [subjects, setSubjects] = useState<any[]>([]);

    // User search
    const [userOptions, setUserOptions] = useState<User[]>([]);
    const [userSearchLoading, setUserSearchLoading] = useState(false);

    // Single User form
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [singleForm, setSingleForm] = useState({ email: "", title: "", body: "" });

    // Multi User form
    const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
    const [multiForm, setMultiForm] = useState({ title: "", body: "" });

    // Bulk form
    const [bulkForm, setBulkForm] = useState<{
        title: string; body: string;
        targetType: "ALL" | "CLASS" | "SUBSCRIPTION";
        classId: string; mediumId: string; subjectId: string; file: File | null;
    }>({ title: "", body: "", targetType: "ALL", classId: "", mediumId: "", subjectId: "", file: null });

    // ── Init metadata ─────────────────────────────────────
    useEffect(() => {
        let m = true;
        (async () => {
            try {
                const [cls, md] = await Promise.all([getClasses(), getMediums()]);
                if (m) { setClasses(Array.isArray(cls) ? cls : []); setMediums(Array.isArray(md) ? md : []); }
            } catch { /* silent */ }
        })();
        return () => { m = false; };
    }, []);

    useEffect(() => {
        let m = true;
        (async () => {
            if (bulkForm.classId || bulkForm.targetType === "SUBSCRIPTION") {
                try {
                    const subs = await getSubjects({
                        classId: bulkForm.classId ? Number(bulkForm.classId) : undefined,
                        mediumId: bulkForm.mediumId ? Number(bulkForm.mediumId) : undefined,
                    });
                    if (m) setSubjects(Array.isArray(subs) ? subs : []);
                } catch { if (m) setSubjects([]); }
            } else { if (m) setSubjects([]); }
        })();
        return () => { m = false; };
    }, [bulkForm.classId, bulkForm.mediumId, bulkForm.targetType]);

    useEffect(() => {
        if ((tabIndex === 1 || tabIndex === 2) && userOptions.length === 0) {
            (async () => {
                setUserSearchLoading(true);
                try {
                    const u = await getUsers({ page: 0, size: 100 });
                    setUserOptions(Array.isArray(u) ? u : (u?.content ?? []));
                } catch { /* silent */ }
                finally { setUserSearchLoading(false); }
            })();
        }
    }, [tabIndex, userOptions.length]);

    // ── Submit handlers ───────────────────────────────────
    const handleSingleSubmit = async () => {
        const email = selectedUser ? selectedUser.email : singleForm.email;
        if (!email || !singleForm.title || !singleForm.body) {
            setSnackbar({ type: "error", msg: "Please provide an email, title, and message body." });
            return;
        }
        setLoading(true);
        try {
            await sendSingleNotification({ email, title: singleForm.title, body: singleForm.body });
            setSnackbar({ type: "success", msg: "Message sent successfully!" });
            setSingleForm({ email: "", title: "", body: "" }); setSelectedUser(null);
        } catch (err: any) {
            setSnackbar({ type: "error", msg: err.message || "Failed to send message." });
        } finally { setLoading(false); }
    };

    const handleMultiSubmit = async () => {
        if (!selectedUsers.length) { setSnackbar({ type: "error", msg: "Select at least one user." }); return; }
        if (!multiForm.title || !multiForm.body) { setSnackbar({ type: "error", msg: "Title and body are required." }); return; }
        setLoading(true);
        try {
            const emails = selectedUsers.map(u => u.email).filter(Boolean);
            await sendUserListNotification({ emails, title: multiForm.title, body: multiForm.body });
            setSnackbar({ type: "success", msg: `Sent to ${emails.length} users!` });
            setMultiForm({ title: "", body: "" }); setSelectedUsers([]);
        } catch (err: any) {
            setSnackbar({ type: "error", msg: err.message || "Failed to send." });
        } finally { setLoading(false); }
    };

    const handleBulkSubmit = async () => {
        if (!bulkForm.title || !bulkForm.body) { setSnackbar({ type: "error", msg: "Title and body are required." }); return; }
        if (bulkForm.targetType === "CLASS" && (!bulkForm.classId || !bulkForm.mediumId)) {
            setSnackbar({ type: "error", msg: "Class and Medium are required for class broadcasts." }); return;
        }
        setLoading(true);
        try {
            const payload: BulkNotificationData = {
                title: bulkForm.title, body: bulkForm.body, targetType: bulkForm.targetType,
                classId: bulkForm.classId ? Number(bulkForm.classId) : null,
                mediumId: bulkForm.mediumId ? Number(bulkForm.mediumId) : null,
                subjectId: bulkForm.subjectId ? Number(bulkForm.subjectId) : null,
            };
            await sendBulkNotification(payload, bulkForm.file);
            setSnackbar({ type: "success", msg: `Broadcast sent to ${bulkForm.targetType} audience!` });
            setBulkForm(p => ({ ...p, title: "", body: "", file: null }));
        } catch (err: any) {
            setSnackbar({ type: "error", msg: err.message || "Broadcast failed." });
        } finally { setLoading(false); }
    };

    const TARGET_LABELS: Record<string, string> = {
        ALL: "All Users", CLASS: "Class & Medium", SUBSCRIPTION: "Subject Subscribers",
    };

    // ── Render ────────────────────────────────────────────
    return (
        <Box sx={{ minHeight: "100vh", background: "#f5f6fa", p: { xs: 2, md: 3 } }}>
            <Box sx={{ maxWidth: 1100, mx: "auto" }}>

                {/* ── Hero Header ── */}
                <Paper elevation={0} sx={{
                    mb: 3, borderRadius: 4, overflow: "hidden",
                    background: `linear-gradient(135deg, ${PRIMARY} 0%, #2a6b6f 60%, ${ACCENT} 100%)`,
                    color: "#fff", animation: `${fadeUp} 0.4s ease-out`, position: "relative",
                }}>
                    <Box sx={{ position: "absolute", top: -50, right: -30, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.05)", pointerEvents: "none" }} />
                    <Box sx={{ position: "absolute", bottom: -20, right: 110, width: 80, height: 80, borderRadius: "50%", background: "rgba(255,255,255,0.07)", pointerEvents: "none" }} />
                    <Box sx={{ p: { xs: 3, md: 4 }, display: "flex", alignItems: "center", gap: 2.5, position: "relative" }}>
                        <Box sx={{ width: 52, height: 52, borderRadius: 2.5, background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <NotifIcon sx={{ fontSize: 28 }} />
                        </Box>
                        <Box>
                            <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: -0.5 }}>Notifications Centre</Typography>
                            <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.3 }}>
                                Send broadcast, class, or individual push notifications
                            </Typography>
                        </Box>
                    </Box>
                </Paper>

                {/* ── Main card ── */}
                <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", overflow: "hidden", animation: `${fadeUp} 0.4s ease-out 60ms both` }}>
                    {/* Tab bar */}
                    <Tabs
                        value={tabIndex}
                        onChange={(_, v) => setTabIndex(v)}
                        variant="fullWidth"
                        sx={{
                            borderBottom: "1px solid", borderColor: "divider", background: "#fafafa",
                            "& .MuiTab-root": { fontWeight: 600, fontSize: "0.82rem", textTransform: "none", py: 2.5, gap: 0.75, "&.Mui-selected": { color: PRIMARY, fontWeight: 800 } },
                            "& .MuiTabs-indicator": { background: `linear-gradient(90deg, ${PRIMARY}, ${ACCENT})`, height: 3, borderRadius: 2 },
                        }}
                    >
                        <Tab icon={<BroadcastIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Broadcast" />
                        <Tab icon={<PersonIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Single User" />
                        <Tab icon={<GroupIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Multi Users" />
                    </Tabs>

                    <Box sx={{ p: { xs: 2.5, md: 4 } }}>

                        {/* ══ TAB 0: BROADCAST ══ */}
                        {tabIndex === 0 && (
                            <Grid container spacing={4}>
                                {/* Left: form */}
                                <Grid size={{ xs: 12, md: 7 }}>
                                    <Stack spacing={3}>
                                        {/* Audience selector */}
                                        <Box>
                                            <SectionLabel icon={<FilterIcon fontSize="small" />}>Target Audience</SectionLabel>
                                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                                <Stack spacing={2}>
                                                    <FormControl size="small" fullWidth>
                                                        <InputLabel>Audience</InputLabel>
                                                        <Select value={bulkForm.targetType} label="Audience"
                                                            onChange={e => setBulkForm({ ...bulkForm, targetType: e.target.value as any, classId: "", mediumId: "", subjectId: "" })}>
                                                            <MenuItem value="ALL">
                                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                                    <GroupIcon fontSize="small" sx={{ color: ACCENT }} />
                                                                    <Box><Typography variant="body2" fontWeight={700}>All Users</Typography><Typography variant="caption" color="text.secondary">Every registered user</Typography></Box>
                                                                </Box>
                                                            </MenuItem>
                                                            <MenuItem value="CLASS">
                                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                                    <FilterIcon fontSize="small" sx={{ color: ACCENT }} />
                                                                    <Box><Typography variant="body2" fontWeight={700}>Class & Medium</Typography><Typography variant="caption" color="text.secondary">Target by class and medium</Typography></Box>
                                                                </Box>
                                                            </MenuItem>
                                                            <MenuItem value="SUBSCRIPTION">
                                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                                    <BroadcastIcon fontSize="small" sx={{ color: ACCENT }} />
                                                                    <Box><Typography variant="body2" fontWeight={700}>Subject Subscribers</Typography><Typography variant="caption" color="text.secondary">Users with active subscriptions</Typography></Box>
                                                                </Box>
                                                            </MenuItem>
                                                        </Select>
                                                    </FormControl>

                                                    {(bulkForm.targetType === "CLASS" || bulkForm.targetType === "SUBSCRIPTION") && (
                                                        <Grid container spacing={1.5}>
                                                            <Grid size={{ xs: 6 }}>
                                                                <FormControl size="small" fullWidth>
                                                                    <InputLabel>Class</InputLabel>
                                                                    <Select value={bulkForm.classId} label="Class" onChange={e => setBulkForm({ ...bulkForm, classId: e.target.value })}>
                                                                        <MenuItem value=""><em>Any</em></MenuItem>
                                                                        {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                                                                    </Select>
                                                                </FormControl>
                                                            </Grid>
                                                            <Grid size={{ xs: 6 }}>
                                                                <FormControl size="small" fullWidth>
                                                                    <InputLabel>Medium</InputLabel>
                                                                    <Select value={bulkForm.mediumId} label="Medium" onChange={e => setBulkForm({ ...bulkForm, mediumId: e.target.value })}>
                                                                        <MenuItem value=""><em>Any</em></MenuItem>
                                                                        {mediums.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
                                                                    </Select>
                                                                </FormControl>
                                                            </Grid>
                                                        </Grid>
                                                    )}

                                                    {bulkForm.targetType === "SUBSCRIPTION" && (
                                                        <FormControl size="small" fullWidth>
                                                            <InputLabel>Subject</InputLabel>
                                                            <Select value={bulkForm.subjectId} label="Subject" onChange={e => setBulkForm({ ...bulkForm, subjectId: e.target.value })}>
                                                                <MenuItem value=""><em>All active subscriptions</em></MenuItem>
                                                                {subjects.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                                                            </Select>
                                                        </FormControl>
                                                    )}

                                                    {/* Audience tag */}
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                                        <Typography variant="caption" color="text.secondary">Sending to:</Typography>
                                                        <Chip label={TARGET_LABELS[bulkForm.targetType]} size="small"
                                                            sx={{ background: "rgba(23,65,67,0.08)", color: PRIMARY, fontWeight: 700, fontSize: "0.7rem" }} />
                                                    </Box>
                                                </Stack>
                                            </Paper>
                                        </Box>

                                        <Divider />

                                        {/* Message composer */}
                                        <Box>
                                            <SectionLabel icon={<BroadcastIcon fontSize="small" />}>Message</SectionLabel>
                                            <Stack spacing={2}>
                                                <TextField label="Notification Title *" size="small" fullWidth
                                                    value={bulkForm.title} onChange={e => setBulkForm({ ...bulkForm, title: e.target.value })} />
                                                <TextField label="Message Body *" size="small" fullWidth multiline rows={4}
                                                    value={bulkForm.body} onChange={e => setBulkForm({ ...bulkForm, body: e.target.value })} />
                                            </Stack>
                                        </Box>

                                        {/* Image attachment */}
                                        <Box>
                                            <SectionLabel icon={<ImageIcon fontSize="small" />}>Image (optional)</SectionLabel>
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <Button variant="outlined" component="label" startIcon={<ImageIcon />}
                                                    sx={{ borderRadius: 2, fontWeight: 600, textTransform: "none", borderColor: "divider" }}>
                                                    {bulkForm.file ? "Change Image" : "Attach Image"}
                                                    <input type="file" accept="image/*" hidden onChange={e => setBulkForm({ ...bulkForm, file: e.target.files?.[0] || null })} />
                                                </Button>
                                                {bulkForm.file && (
                                                    <Chip icon={<ImageIcon />} label={bulkForm.file.name}
                                                        onDelete={() => setBulkForm({ ...bulkForm, file: null })}
                                                        size="small" sx={{ maxWidth: 200, fontWeight: 600 }} />
                                                )}
                                            </Stack>
                                        </Box>

                                        <Button variant="contained" size="large" startIcon={loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <SendIcon />}
                                            onClick={handleBulkSubmit} disabled={loading}
                                            sx={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, fontWeight: 700, borderRadius: 2, px: 5, py: 1.4, alignSelf: "flex-start", boxShadow: "0 4px 14px rgba(23,65,67,0.3)" }}>
                                            Send Broadcast
                                        </Button>
                                    </Stack>
                                </Grid>

                                {/* Right: preview */}
                                <Grid size={{ xs: 12, md: 5 }}>
                                    <NotifPreview
                                        title={bulkForm.title}
                                        body={bulkForm.body}
                                        imageUrl={bulkForm.file ? URL.createObjectURL(bulkForm.file) : undefined}
                                    />
                                </Grid>
                            </Grid>
                        )}

                        {/* ══ TAB 1: SINGLE USER ══ */}
                        {tabIndex === 1 && (
                            <Grid container spacing={4}>
                                <Grid size={{ xs: 12, md: 7 }}>
                                    <Stack spacing={3}>
                                        <Box>
                                            <SectionLabel icon={<PersonIcon fontSize="small" />}>Select Recipient</SectionLabel>
                                            <Stack spacing={2}>
                                                <Autocomplete
                                                    options={userOptions}
                                                    loading={userSearchLoading}
                                                    getOptionLabel={o => `${o.displayName || "No Name"} (${o.email})`}
                                                    value={selectedUser}
                                                    onChange={(_, v) => { setSelectedUser(v); if (v) setSingleForm(p => ({ ...p, email: v.email })); }}
                                                    renderInput={params => (
                                                        <TextField {...params} size="small" label="Search by name or email"
                                                            InputProps={{ ...params.InputProps, endAdornment: <>{userSearchLoading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</> }} />
                                                    )}
                                                />
                                                {selectedUser && (
                                                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, background: "#f0fafa", borderColor: "rgba(61,158,164,0.3)", display: "flex", alignItems: "center", gap: 1.5 }}>
                                                        <Box sx={{ width: 36, height: 36, borderRadius: "50%", background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "0.8rem", fontWeight: 800, flexShrink: 0 }}>
                                                            {selectedUser.displayName?.[0]?.toUpperCase() || "?"}
                                                        </Box>
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography variant="body2" fontWeight={700} color={PRIMARY} noWrap>{selectedUser.displayName}</Typography>
                                                            <Typography variant="caption" color="text.secondary" noWrap>{selectedUser.email}</Typography>
                                                        </Box>
                                                        <Button size="small" onClick={() => { setSelectedUser(null); setSingleForm(p => ({ ...p, email: "" })); }}
                                                            sx={{ color: "error.main", minWidth: 0 }}><ClearIcon fontSize="small" /></Button>
                                                    </Paper>
                                                )}
                                                {!selectedUser && (
                                                    <TextField label="Or enter email manually" size="small" fullWidth
                                                        value={singleForm.email} onChange={e => setSingleForm({ ...singleForm, email: e.target.value })}
                                                        placeholder="user@example.com" />
                                                )}
                                            </Stack>
                                        </Box>

                                        <Divider />

                                        <Box>
                                            <SectionLabel icon={<BroadcastIcon fontSize="small" />}>Message</SectionLabel>
                                            <Stack spacing={2}>
                                                <TextField label="Notification Title *" size="small" fullWidth
                                                    value={singleForm.title} onChange={e => setSingleForm({ ...singleForm, title: e.target.value })} />
                                                <TextField label="Message Body *" size="small" fullWidth multiline rows={4}
                                                    value={singleForm.body} onChange={e => setSingleForm({ ...singleForm, body: e.target.value })} />
                                            </Stack>
                                        </Box>

                                        <Button variant="contained" size="large" startIcon={loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <SendIcon />}
                                            onClick={handleSingleSubmit} disabled={loading}
                                            sx={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, fontWeight: 700, borderRadius: 2, py: 1.4, boxShadow: "0 4px 14px rgba(23,65,67,0.3)" }}>
                                            Send Message
                                        </Button>
                                    </Stack>
                                </Grid>
                                <Grid size={{ xs: 12, md: 5 }}>
                                    <NotifPreview title={singleForm.title} body={singleForm.body} />
                                </Grid>
                            </Grid>
                        )}

                        {/* ══ TAB 2: MULTI USERS ══ */}
                        {tabIndex === 2 && (
                            <Grid container spacing={4}>
                                <Grid size={{ xs: 12, md: 7 }}>
                                    <Stack spacing={3}>
                                        <Box>
                                            <SectionLabel icon={<GroupIcon fontSize="small" />}>Select Recipients</SectionLabel>
                                            <Stack spacing={1.5}>
                                                <Autocomplete
                                                    multiple
                                                    options={userOptions}
                                                    loading={userSearchLoading}
                                                    getOptionLabel={o => `${o.displayName || "No Name"} (${o.email})`}
                                                    value={selectedUsers}
                                                    onChange={(_, v) => setSelectedUsers(v)}
                                                    renderInput={params => (
                                                        <TextField {...params} size="small" label="Search and add users"
                                                            placeholder={selectedUsers.length ? "Add more…" : "Type to search…"} />
                                                    )}
                                                    renderTags={(val, getTagProps) =>
                                                        val.map((opt, i) => (
                                                            <Chip {...getTagProps({ index: i })} label={opt.displayName || opt.email} size="small"
                                                                sx={{ background: "rgba(23,65,67,0.1)", color: PRIMARY, fontWeight: 700 }} />
                                                        ))
                                                    }
                                                />
                                                {selectedUsers.length > 0 && (
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <Chip label={`${selectedUsers.length} recipients selected`} size="small"
                                                            sx={{ background: "rgba(67,233,123,0.15)", color: "#166534", fontWeight: 700, fontSize: "0.72rem" }} />
                                                        <Button size="small" onClick={() => setSelectedUsers([])} sx={{ color: "error.main", textTransform: "none", fontSize: "0.75rem" }}>
                                                            Clear all
                                                        </Button>
                                                    </Box>
                                                )}
                                            </Stack>
                                        </Box>

                                        <Divider />

                                        <Box>
                                            <SectionLabel icon={<BroadcastIcon fontSize="small" />}>Message</SectionLabel>
                                            <Stack spacing={2}>
                                                <TextField label="Notification Title *" size="small" fullWidth
                                                    value={multiForm.title} onChange={e => setMultiForm({ ...multiForm, title: e.target.value })} />
                                                <TextField label="Message Body *" size="small" fullWidth multiline rows={4}
                                                    value={multiForm.body} onChange={e => setMultiForm({ ...multiForm, body: e.target.value })} />
                                            </Stack>
                                        </Box>

                                        <Button variant="contained" size="large"
                                            startIcon={loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <SendIcon />}
                                            onClick={handleMultiSubmit} disabled={loading || !selectedUsers.length}
                                            sx={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, fontWeight: 700, borderRadius: 2, py: 1.4, boxShadow: "0 4px 14px rgba(23,65,67,0.3)" }}>
                                            {selectedUsers.length > 0 ? `Send to ${selectedUsers.length} User${selectedUsers.length > 1 ? "s" : ""}` : "Send to Selected Users"}
                                        </Button>
                                    </Stack>
                                </Grid>
                                <Grid size={{ xs: 12, md: 5 }}>
                                    <NotifPreview title={multiForm.title} body={multiForm.body} />
                                </Grid>
                            </Grid>
                        )}
                    </Box>
                </Paper>
            </Box>

            {/* ── Snackbar ── */}
            <Snackbar open={!!snackbar} autoHideDuration={5000} onClose={() => setSnackbar(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
                <Alert onClose={() => setSnackbar(null)} severity={snackbar?.type ?? "info"} variant="filled" sx={{ borderRadius: 2 }}>
                    {snackbar?.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}